import { atom } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import { TradeType, marketType } from '../../types/trade.type';

// Initialize recoil-persist for trade atoms
const { persistAtom } = recoilPersist({
  key: 'recoil-persist-trade',
  storage: localStorage,
});

export const limitOrderDefaultPayout = '60';
export const defaultSelectedTime = '00:15';

export enum tradePanelPosition {
  Left,
  Right,
}

export const refreshInterval = 1000;

export const isTestnet = import.meta.env.VITE_ENV?.toLowerCase() === 'testnet';

export function HHMMToSeconds(time: string) {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 3600 + Number(minutes) * 60;
}

// Chart control settings atom (persisted)
export const chartControlsSettingsAtom = atom<{ earlyCloseConfirmation: boolean; loDragging: boolean }>({
  key: 'chartControlsSettings',
  default: { earlyCloseConfirmation: true, loDragging: true },
  effects_UNSTABLE: [persistAtom],
});

// Time selector atom (persisted)
export const timeSelectorAtom = atom<{ HHMM: string; seconds: number }>({
  key: 'timeSelector',
  default: {
    HHMM: defaultSelectedTime,
    seconds: HHMMToSeconds(defaultSelectedTime),
  },
  effects_UNSTABLE: [persistAtom],
});

// Trade size atom (persisted)
export const tradeSizeAtom = atom<string>({
  key: 'tradeSize',
  default: '0.1',
  effects_UNSTABLE: [persistAtom],
});

// Active pool object atom (persisted)
export const activePoolObjAtom = atom<{ activePool: string }>({
  key: 'activePoolObj',
  default: { activePool: isTestnet ? 'ARB' : 'WHYPE' },
  effects_UNSTABLE: [persistAtom],
});

// Asset selector pool atom
export const assetSelectorPoolAtom = atom<string>({
  key: 'assetSelectorPool',
  default: 'USDC',
});

// Pinned markets atom (persisted)
export const pinnedMarketsAtom = atom<string[]>({
  key: 'pinnedMarkets',
  default: [''],
  effects_UNSTABLE: [persistAtom],
});

// Favorite markets atoms (persisted)
export const favouriteMarketsAtom = atom<string[]>({
  key: 'favouriteMarkets',
  default: [''],
  effects_UNSTABLE: [persistAtom],
});

export const favouritePerpsMarketsAtom = atom<string[]>({
  key: 'favouritePerpsMarkets',
  default: [''],
  effects_UNSTABLE: [persistAtom],
});

// Categories atom
export const categoriesAtom = atom<string>({
  key: 'categories',
  default: 'all',
});

// Search bar atom
export const searchBarAtom = atom<string>({
  key: 'searchBar',
  default: '',
});

// Radio value atom
export const radioValueAtom = atom<string>({
  key: 'radioValue',
  default: 'WHYPE',
});

// Chart number atom (persisted)
export const chartNumberAtom = atom<number>({
  key: 'chartNumber',
  default: 1,
  effects_UNSTABLE: [persistAtom],
});

// Trade type atom
export const tradeTypeAtom = atom<'Market' | 'Limit'>({
  key: 'tradeType',
  default: 'Market',
});

// Limit order strike atom
export const limitOrderStrikeAtom = atom<string | null>({
  key: 'limitOrderStrike',
  default: null,
});

// Limit order payout atom (persisted)
export const LimitOrderPayoutAtom = atom<string>({
  key: 'limitOrderPayout',
  default: limitOrderDefaultPayout,
  effects_UNSTABLE: [persistAtom],
});

// Table visibility atom
export const isTableShownAtom = atom<boolean>({
  key: 'isTableShown',
  default: false,
});

// Selected order to edit atom
export const selectedOrderToEditAtom = atom<{
  trade: TradeType;
  market: marketType;
  default?: { strike: string };
} | null>({
  key: 'selectedOrderToEdit',
  default: null,
});

// Visualized data atom
export const visualizeddAtom = atom<number[]>({
  key: 'visualizedd',
  default: [],
});

// Forex timings modal atom
export const ForexTimingsModalAtom = atom<{
  isOpen: boolean;
  marketType: number;
}>({
  key: 'forexTimingsModal',
  default: { isOpen: false, marketType: 0 },
});

// Onboarding animation atom
export const showOnboardingAnimationAtom = atom<boolean>({
  key: 'showOnboardingAnimation',
  default: false,
});

// Queue TS to price atom (persisted)
export const queuets2priceAtom = atom<Record<string | number, number>>({
  key: 'queuets2price',
  default: {},
  effects_UNSTABLE: [persistAtom],
});

// Close loading atom
export const closeLoadingAtom = atom<{ [key: number]: 1 | 2 | null }>({
  key: 'closeLoading',
  default: {},
});

// Share modal atoms
export const ShareStateAtom = atom<{ isOpen: boolean }>({
  key: 'shareState',
  default: { isOpen: false },
});

type ShareBetType = {
  trade: TradeType | null;
  expiryPrice: string | null;
  poolInfo: null;
  market: marketType | null;
};

export const ShareBetAtom = atom<ShareBetType>({
  key: 'shareBet',
  default: {
    trade: null,
    expiryPrice: null,
    poolInfo: null,
    market: null,
  },
});

// Approve modal atom
export const approveModalAtom = atom<boolean>({
  key: 'approveModal',
  default: false,
});

// Trade table pagination atoms
export const historyTableActivePage = atom<number>({
  key: 'historyTableActivePage',
  default: 1,
});

export const cancelTableActivePage = atom<number>({
  key: 'cancelTableActivePage',
  default: 1,
});

export const platformActiveTableActivePage = atom<number>({
  key: 'platformActiveTableActivePage',
  default: 1,
});

export const platformHistoryTableActivePage = atom<number>({
  key: 'platformHistoryTableActivePage',
  default: 1,
});

export const platformCancelTableActivePage = atom<number>({
  key: 'platformCancelTableActivePage',
  default: 1,
});

// Trade inspect mobile atom
export const tradeInspectMobileAtom = atom<{ trade?: TradeType }>({
  key: 'tradeInspectMobile',
  default: {},
});

// Close confirmation modal atom
export const closeConfirmationModalAtom = atom<TradeType | false>({
  key: 'closeConfirmationModal',
  default: false,
});

// Rerender position atom
export const rerenderPositionAtom = atom<number>({
  key: 'rerenderPosition',
  default: 0,
});

// User education atom
export const isUserEducatedAtom = atom<{ mobileChainSwitchingIssue: boolean }>({
  key: 'isUserEducated',
  default: { mobileChainSwitchingIssue: false },
});

// Buy and sell price atoms
export const buyPriceAtom = atom<string | null>({
  key: 'buyPrice',
  default: null,
});

export const sellPriceAtom = atom<string | null>({
  key: 'sellPrice',
  default: null,
});
